<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useValues from "../../use/useValues"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const values = computed(() => store.state.values)
const allAttributes = computed(() => store.state.allAttributes)

const {
  search,
  attributeId,
  perPage,
  sortField,
  sortDirection,
  getValues,
  sortValues,
} = useValues()
const { handleInput } = useDebouncedFunction(getValues)

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getValues()
  store.dispatch("getAllAttributes")
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getValues(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editValue = (val) => {
  emit("clickEdit", val)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی نام مقدار ویژگی..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getValues(null)"
              v-model="attributeId"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="">انتخاب ویژگی</option>
              <option
                v-for="attribute in allAttributes.data"
                :value="attribute.id"
                :key="attribute.id"
              >
                {{ attribute.name }}
              </option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getValues(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="valueTable">
          <thead class="table-light text-muted">
            <tr>
              <TableHeaderCell
                field="value_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortValues('value_name')"
              >
                نام مقدار ویژگی
              </TableHeaderCell>

              <TableHeaderCell
                field="attribute_id"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortValues('attribute_id')"
              >
                نام ویژگی
              </TableHeaderCell>

              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortValues('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody class="list" v-if="values.loading || !values.data.length">
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="values.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ مقدار ویژگی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(value, index) of values.data" :key="index">
              <td class="value_name">{{ value.name }}</td>
              <td class="attribute_name">{{ value.attribute_name }}</td>
              <td class="date">{{ toPersianDateTime(value.created_at) }}</td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editValue(value)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                  <li
                    class="list-inline-item"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Remove"
                  >
                    <b-link
                      class="text-danger d-inline-block remove-item-btn"
                      @click="deletedata(item)"
                    >
                      <i class="ri-delete-bin-5-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="values.total > values.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of values.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
